/**
 * detectGcPdfType — content-based classifier for GameChanger PDF exports.
 *
 * WHY THIS EXISTS: GameChanger exports BOTH the Scorebook PDF and the Box Score
 * (Game Stats Sheet) PDF with the SAME filename, so we can never route by name.
 * This sniffs the text layer and decides which one it is, so a single smart
 * dropzone (and the iOS share target) can accept whatever the coach drops, in
 * any order. Pure + Node-safe (operates on PdfPage[], no pdfjs runtime import).
 *
 * Fingerprints (verified against real KC exports):
 *  - Box score  = single page with BATTING + PITCHING section headers and inline
 *                 "P-S:" / "BF:" note labels. Two-column box score, no diamond grid.
 *  - Scorebook  = multi-page diamond grid: an inn/pos header, lone "B"/"S" pitch
 *                 tokens repeated down each at-bat cell, "#<jersey>" result cells.
 */

import { PdfPage } from './types';

export type GcPdfType = 'scorebook' | 'gameStatsSheet' | 'unknown';

export interface GcPdfDetection {
  type: GcPdfType;
  confidence: 'high' | 'low';
  reason: string;
}

/** Count text items whose trimmed string exactly equals `s`, across all pages. */
function countExact(pages: PdfPage[], s: string): number {
  let n = 0;
  for (const pg of pages) for (const it of pg.items) if (it.str.trim() === s) n++;
  return n;
}

/** Does any item's trimmed string contain `sub` (case-sensitive)? */
function hasSubstring(pages: PdfPage[], sub: string): boolean {
  for (const pg of pages) for (const it of pg.items) if (it.str.includes(sub)) return true;
  return false;
}

/** Count "#<digits>" jersey-cell tokens (the scorebook tags every result cell). */
function countJerseyTokens(pages: PdfPage[]): number {
  let n = 0;
  for (const pg of pages) for (const it of pg.items) if (/^#\d+\b/.test(it.str.trim())) n++;
  return n;
}

export function detectGcPdfType(pages: PdfPage[]): GcPdfDetection {
  if (!pages.length) return { type: 'unknown', confidence: 'low', reason: 'Empty PDF.' };

  // ── Box score signals ──────────────────────────────────────────────────────
  // BATTING + PITCHING headers + at least one P-S/BF note = unambiguous box score.
  const hasBatting = countExact(pages, 'BATTING') > 0;
  const hasPitching = countExact(pages, 'PITCHING') > 0;
  const hasPSorBF = hasSubstring(pages, 'P-S:') || hasSubstring(pages, 'BF:');
  if (hasBatting && hasPitching && hasPSorBF) {
    return { type: 'gameStatsSheet', confidence: 'high', reason: 'BATTING/PITCHING sections with P-S/BF notes.' };
  }

  // ── Scorebook signals ──────────────────────────────────────────────────────
  // The diamond grid sprays many lone "B" and "S" pitch tokens and "#<jersey>"
  // result cells; the box score has essentially none of these.
  const bTokens = countExact(pages, 'B');
  const sTokens = countExact(pages, 'S');
  const jerseyCells = countJerseyTokens(pages);
  const scorebookish = bTokens >= 8 && sTokens >= 8 && jerseyCells >= 4;
  if (scorebookish) {
    return { type: 'scorebook', confidence: 'high', reason: `Diamond-grid pitch tokens (B×${bTokens}, S×${sTokens}) and ${jerseyCells} jersey cells.` };
  }

  // ── Weak fallbacks (one signal only) ───────────────────────────────────────
  if (hasBatting && hasPitching) {
    return { type: 'gameStatsSheet', confidence: 'low', reason: 'BATTING/PITCHING headers but no P-S/BF notes.' };
  }
  if (jerseyCells >= 4 && (bTokens >= 4 || sTokens >= 4)) {
    return { type: 'scorebook', confidence: 'low', reason: 'Some jersey cells and pitch tokens, but a thin grid.' };
  }

  return { type: 'unknown', confidence: 'low', reason: 'No GameChanger scorebook or box-score fingerprint matched.' };
}
